import { API_BASE_URL, ApiError } from './client';
import type { AccessBasis, AudienceIncludes } from './conversations';

/**
 * One of an event's conversations — TROOP-179.
 *
 * **Every row here is one this reader may see.** That is not a filter the client applies; the
 * server never builds the fuller list. A topic's *existence* is the disclosure this feature is
 * about — an adult leaders' thread on a campout is a fact a Scout must not learn — so an
 * unreadable topic contributes no row and no number. Nothing on this page may be totalled against
 * anything the server did not send.
 */
export interface EventTopic {
  conversationId: string;
  /** A short slug, unique within the event. The general discussion's is `general`. */
  topicKey: string;
  /** What people read. The description a leader typed. */
  title: string;
  audienceMode: 'RosterScoped' | 'Explicit';
  audienceIncludes: AudienceIncludes;
  /** Why *you* may see it, from `ConversationAccessPolicy`. */
  basis: AccessBasis;
  unreadCount: number;
  messageCount: number;
  lastMessageAt: string | null;
  /** The one every event has, created with it. Always first, and always the default. */
  isGeneral: boolean;
  /**
   * The group this topic is for, when it is for one (TROOP-180).
   *
   * The *name* travels rather than only the id, because a client resolving it would need the
   * unit's whole group list to render one line. What a unit calls a group — Patrol, Den, Crew —
   * is still the unit's own word from the session payload (rule 16); this is the group's name.
   */
  audienceSubgroupId?: string | null;
  audienceSubgroupName?: string | null;
}

export interface EventTopics {
  rows: EventTopic[];
  /** Whether to offer the "open a topic" control. False on the guardian's door, always. */
  canCreate: boolean;
}

/**
 * `unitId` absent picks the guardian's door — the same two-door switch the docket and photo
 * clients use. Never a second set of rules, only a second path: a parents' topic is read from
 * `/family`, because a guardian holds no membership and `/units/` 404s them.
 */
const base = (unitId: string | undefined, eventId: string) =>
  unitId
    ? `${API_BASE_URL}/units/${unitId}/events/${eventId}/topics`
    : `${API_BASE_URL}/family/events/${eventId}/topics`;

/** Null when there is no such event, or none this caller may read. */
export async function loadEventTopics(
  unitId: string | undefined,
  eventId: string,
): Promise<EventTopics | null> {
  const response = await fetch(base(unitId, eventId), { credentials: 'include' });

  if (response.status === 404) return null;

  if (!response.ok) {
    throw new ApiError(response.status, `GET topics for ${eventId} failed`);
  }

  return (await response.json()) as EventTopics;
}

/**
 * Opens another conversation on the event.
 *
 * The audience is the whole point: `AdultsOnly` is the parents-and-leaders room, `OfficersOnly` is
 * the adult leaders' one and derives no guardians at all, and a patrol leaders' council is
 * `Explicit` with its members named. The server runs every invariant on the result and each
 * refusal arrives with a sentence to render — the screen is not the enforcement point.
 */
export async function createEventTopic(
  unitId: string,
  eventId: string,
  input: {
    key: string;
    title: string;
    audienceMode: 'RosterScoped' | 'Explicit';
    audienceIncludes: AudienceIncludes;
    /** For an `Explicit` audience. The creator is added server-side either way. */
    participantIds?: string[];
    /**
     * Narrows a roster-scoped audience to one group (TROOP-180). Refused alongside `AdultsOnly`
     * and `OfficersOnly` — those stay unit-wide by design — and alongside `Explicit`, whose
     * members are the list itself.
     */
    audienceSubgroupId?: string | null;
  },
): Promise<{ conversationId: string; key: string }> {
  const response = await fetch(base(unitId, eventId), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    let problem: { title?: string; detail?: string } = {};
    try {
      problem = await response.json();
    } catch {
      // Non-JSON body; the status is enough.
    }

    // The server's own sentence, always. Every refusal here names something somebody can act on --
    // a Pack that holds no young members, a topic name already taken, the two-adult quorum.
    throw new ApiError(
      response.status,
      problem.detail ?? problem.title ?? `Could not open the topic (${response.status}).`,
    );
  }

  return (await response.json()) as { conversationId: string; key: string };
}
